import React, { lazy, Suspense } from "react";
import { BrowserRouter as Router, Route, Routes, Link } from 'react-router-dom';
import Home from './components/home/Home';
import Play from './playy/Home';
import GameApp from './playy/GameApp';
import Login from './components/Login/Login';
import Signup from './components/Login/Signup';
import PageTransition from './components/PageTransition';
import { UserDataProvider } from './components/UserDataContext';
import Nav from './components/nav';
import GameInviteToast from './components/GameInviteToast';
import FooterLinks from './components/FooterLinks';
import InstallPrompt from './components/InstallPrompt';
import ErrorBoundary from './components/ErrorBoundary';
import GlobeSpinner from './components/GlobeSpinner';
import BackgroundEffects from './components/BackgroundEffects';
import './playy/app.css'

// Lazy-loaded routes keep their libraries (chart.js, chess-eco-codes) off the
// critical path. They only download when the user actually visits the page.
const Analysis = lazy(() => import('./components/Analysis/Analysis'));
const Puzzles = lazy(() => import('./components/Puzzles/Puzzles'));
const Profile = lazy(() => import('./components/Profile/Profile'));
const About = lazy(() => import('./components/StaticPages/About'));
const Contact = lazy(() => import('./components/StaticPages/Contact'));
const Privacy = lazy(() => import('./components/StaticPages/Privacy'));
const Terms = lazy(() => import('./components/StaticPages/Terms'));
const Guides = lazy(() => import('./components/StaticPages/Guides'));
const OpeningsGuide = lazy(() => import('./components/StaticPages/OpeningsGuide'));
const EndgameBasics = lazy(() => import('./components/StaticPages/EndgameBasics'));
const TacticsGuide = lazy(() => import('./components/StaticPages/TacticsGuide'));
const ChessNotation = lazy(() => import('./components/StaticPages/ChessNotation'));
const EngineAnalysis = lazy(() => import('./components/StaticPages/EngineAnalysis'));
const ImproveGuide = lazy(() => import('./components/StaticPages/ImproveGuide'));
const BlogIndex = lazy(() => import('./components/Blog/BlogIndex'));
const PostKnightFork = lazy(() => import('./components/Blog/PostKnightFork'));
const PostBeginnerBlunders = lazy(() => import('./components/Blog/PostBeginnerBlunders'));
const PostPawnStructure = lazy(() => import('./components/Blog/PostPawnStructure'));
const PostFirstEndgame = lazy(() => import('./components/Blog/PostFirstEndgame'));
const PostTimeManagement = lazy(() => import('./components/Blog/PostTimeManagement'));
const PostTimeControls = lazy(() => import('./components/Blog/PostTimeControls'));
const PostAnalyzeYourGames = lazy(() => import('./components/Blog/PostAnalyzeYourGames'));
const PostOpeningsByDifficulty = lazy(() => import('./components/Blog/PostOpeningsByDifficulty'));
const PostEngineEvals = lazy(() => import('./components/Blog/PostEngineEvals'));
const Ninja = lazy(() => import('./components/Ninja/Ninja'));

function RouteFallback() {
    return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 20px' }}>
            <GlobeSpinner size={60} />
        </div>
    );
}

function NotFound() {
    return (
        <main style={{ maxWidth: 600, margin: '60px auto', padding: '20px 16px', color: '#fff', textAlign: 'center', fontFamily: "'Space Grotesk', sans-serif", lineHeight: 1.6 }}>
            <h1 style={{ color: '#ffd54a', fontSize: 24 }}>Page not found</h1>
            <p style={{ color: '#c0ccff' }}>The page you're looking for doesn't exist or has moved.</p>
            <p><Link to="/" style={{ color: '#ffd54a', fontWeight: 600 }}>Return home</Link></p>
        </main>
    );
}

const App = () => {
    return (
        <Router>
        <UserDataProvider>
        <BackgroundEffects />
        <Nav />
        <GameInviteToast />
        <PageTransition>
        {(loc) => (
        <ErrorBoundary key={loc.pathname}>
        <Suspense fallback={<RouteFallback />}>
        <Routes location={loc}>
        <Route path="/" element={<Home/>} />
        <Route path="/analysis/:id" element={<Analysis/>} />
        <Route path="/analysis" element={<Analysis/>} />
        <Route path="/about" element={<About/>} />
        <Route path="/contact" element={<Contact/>} />
        <Route path="/privacy" element={<Privacy/>} />
        <Route path="/terms" element={<Terms/>} />
        <Route path="/guides" element={<Guides/>} />
        <Route path="/guides/openings" element={<OpeningsGuide/>} />
        <Route path="/guides/endgames" element={<EndgameBasics/>} />
        <Route path="/guides/tactics" element={<TacticsGuide/>} />
        <Route path="/guides/notation" element={<ChessNotation/>} />
        <Route path="/guides/analysis" element={<EngineAnalysis/>} />
        <Route path="/guides/improve" element={<ImproveGuide/>} />
        <Route path="/blog" element={<BlogIndex/>} />
        <Route path="/blog/spot-knight-fork" element={<PostKnightFork/>} />
        <Route path="/blog/common-beginner-blunders" element={<PostBeginnerBlunders/>} />
        <Route path="/blog/pawn-structure-basics" element={<PostPawnStructure/>} />
        <Route path="/blog/first-endgame" element={<PostFirstEndgame/>} />
        <Route path="/blog/time-management-online" element={<PostTimeManagement/>} />
        <Route path="/blog/bullet-blitz-rapid" element={<PostTimeControls/>} />
        <Route path="/blog/analyze-your-games" element={<PostAnalyzeYourGames/>} />
        <Route path="/blog/openings-by-difficulty" element={<PostOpeningsByDifficulty/>} />
        <Route path="/blog/engine-evaluations" element={<PostEngineEvals/>} />
        <Route path="/play" element={<Play/>} />
        <Route path="/game/:id" element={<GameApp/>} />
        <Route path="/login" element={<Login/>} />
        <Route path="/signup" element={<Signup/>} />
        <Route path="/puzzles" element={<Puzzles/>} />
        <Route path="/profile" element={<Profile/>} />
        <Route path="/ninja" element={<Ninja/>} />
        <Route path="*" element={<NotFound/>} />
        </Routes>
        </Suspense>
        </ErrorBoundary>
        )}
        </PageTransition>
        <FooterLinks />
        <InstallPrompt />
        </UserDataProvider>
    </Router>
    );
};

export default App;
